import React, { useState } from "react"

import { format } from "date-fns"
import { Calendar, Check, Loader2, RefreshCw } from "lucide-react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  PersonMissingContractStartDate,
  usePeopleMissingContractStartDate,
} from "@/hooks/usePeopleMissingContractStartDate"
import { useUpdateContractStartDate } from "@/hooks/useUpdateContractStartDate"
import DashboardLayout from "@/layouts/DashboardLayout"

const UpdateContractStartDate: React.FC = () => {
  const { updateContractStartDate, isLoading } = useUpdateContractStartDate()
  const { data: people, isLoading: isLoadingPeople, refetch } = usePeopleMissingContractStartDate()
  const [updatingPersonIds, setUpdatingPersonIds] = useState<Set<string>>(new Set())

  const handleUpdate = async (person: PersonMissingContractStartDate) => {
    // Get the onboarded_at date from the application
    const application = person.applications[0]
    if (!application || !application.onboarded_at) {
      toast.error("Error", { description: "No onboarded date found for this person's application" })
      return
    }

    setUpdatingPersonIds((prev) => new Set(prev).add(person.id))

    try {
      // Extract date from onboarded_at (it's a timestamp, we need just the date part)
      const onboardedDate = new Date(application.onboarded_at)
      const dateString = format(onboardedDate, "yyyy-MM-dd")

      await updateContractStartDate({
        personId: person.id,
        contractStartDate: dateString,
        applicationId: application.id,
      })

      toast.success("Success", {
        description: `Updated contract start date to ${format(onboardedDate, "PPP")} for ${person.first_name || ""} ${person.surname || ""}`,
      })

      refetch()
    } catch (err) {
      toast.error("Error", {
        description: err instanceof Error ? err.message : "Failed to update contract start date",
      })
    } finally {
      setUpdatingPersonIds((prev) => {
        const next = new Set(prev)
        next.delete(person.id)
        return next
      })
    }
  }

  const handleBulkUpdate = async () => {
    if (!people || people.length === 0) return

    // Filter people who have onboarded_at dates
    const peopleWithOnboardedDates = people.filter((person) => person.applications[0]?.onboarded_at)

    if (peopleWithOnboardedDates.length === 0) {
      toast.error("No Updates Available", {
        description: "No people have onboarded dates to update",
      })
      return
    }

    const personIds = new Set(peopleWithOnboardedDates.map((p) => p.id))
    setUpdatingPersonIds(personIds)

    try {
      // Split people into chunks of 10
      const chunkSize = 10
      const chunks: PersonMissingContractStartDate[][] = []
      for (let i = 0; i < peopleWithOnboardedDates.length; i += chunkSize) {
        chunks.push(peopleWithOnboardedDates.slice(i, i + chunkSize))
      }

      let successCount = 0
      let errorCount = 0

      // Process each chunk with a 5-second delay between chunks
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i]

        // Process all items in the current chunk
        const results = await Promise.allSettled(
          chunk.map((person) => {
            const application = person.applications[0]
            if (!application || !application.onboarded_at) {
              return Promise.reject(new Error("No onboarded date"))
            }

            const onboardedDate = new Date(application.onboarded_at)
            const dateString = format(onboardedDate, "yyyy-MM-dd")

            return updateContractStartDate({
              personId: person.id,
              contractStartDate: dateString,
              applicationId: application.id,
            }).catch((err) => {
              console.error(`Failed to update ${person.first_name} ${person.surname}:`, err)
              throw err
            })
          }),
        )

        // Count successes and errors
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            successCount++
          } else {
            errorCount++
          }
        })

        // Wait 5 seconds before processing the next chunk (except for the last chunk)
        if (i < chunks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 5000))
        }
      }

      // Show summary toast
      if (errorCount === 0) {
        toast.success("Success", {
          description: `Successfully updated ${successCount} contract start dates`,
        })
      } else {
        toast.error("Partial Success", {
          description: `Updated ${successCount} contract start dates. ${errorCount} failed.`,
        })
      }

      // Refetch to update the list
      refetch()
    } catch (err) {
      toast.error("Error", {
        description: err instanceof Error ? err.message : "Failed to update contract start dates",
      })
    } finally {
      setUpdatingPersonIds(new Set())
    }
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">Update Contract Start Date</h1>
              {!isLoadingPeople && people !== undefined && (
                <Badge variant="outline" className="text-base px-3 py-1">
                  {people.length} {people.length === 1 ? "person" : "people"} missing start date
                </Badge>
              )}
            </div>
            {people && people.length > 0 && (
              <Button
                onClick={handleBulkUpdate}
                disabled={isLoading || updatingPersonIds.size > 0}
                variant="default"
              >
                {isLoading || updatingPersonIds.size > 0 ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Update All ({people.length})
                  </>
                )}
              </Button>
            )}
          </div>
          <p className="text-muted-foreground">
            Active people who are missing a contract start date, or have a contract end date but no
            start date
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>People Missing Contract Start Date</CardTitle>
                <CardDescription>
                  These active people need a contract start date set
                </CardDescription>
              </div>
              {!isLoadingPeople && people && (
                <Badge variant="secondary" className="text-lg px-3 py-1">
                  {people.length} {people.length === 1 ? "person" : "people"}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingPeople ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Loading people...</span>
              </div>
            ) : !people || people.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Check className="h-12 w-12 mx-auto mb-3 text-green-500" />
                <p className="text-lg font-medium">No people found</p>
                <p className="text-sm">All active people have contract start dates</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Person</TableHead>
                      <TableHead>Person ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Contract Start Date</TableHead>
                      <TableHead>Contract End Date</TableHead>
                      <TableHead>Application</TableHead>
                      <TableHead>Onboarded At</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {people.map((person) => {
                      const isUpdating = updatingPersonIds.has(person.id)
                      const hasStartDate = !!person.contract_start_date
                      const hasEndDate = !!person.contract_end_date

                      return (
                        <TableRow key={person.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {person.first_name} {person.surname}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {person.email || "No email"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-palette-jungleGreen100 px-2 py-1 rounded">
                              {person.person_id || "N/A"}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant={person.status === "Active" ? "default" : "secondary"}>
                              {person.status || "N/A"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={hasStartDate ? "secondary" : "destructive"}>
                              {hasStartDate
                                ? format(new Date(person.contract_start_date!), "PPP")
                                : "Not set"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={hasEndDate ? "outline" : "secondary"}>
                              {hasEndDate
                                ? format(new Date(person.contract_end_date!), "PPP")
                                : "Not set"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {person.applications.length > 0 ? (
                              <code className="text-xs bg-palette-jungleGreen100 px-2 py-1 rounded">
                                {person.applications[0].application_id}
                              </code>
                            ) : (
                              <span className="text-sm text-muted-foreground">No application</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {person.applications.length > 0 &&
                            person.applications[0].onboarded_at ? (
                              <span className="text-sm">
                                {format(new Date(person.applications[0].onboarded_at), "PPP")}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">N/A</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => handleUpdate(person)}
                              disabled={
                                isUpdating || isLoading || !person.applications[0]?.onboarded_at
                              }
                              variant="default"
                            >
                              {isUpdating ? (
                                <>
                                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <Calendar className="mr-2 h-3 w-3" />
                                  Update Date
                                </>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default UpdateContractStartDate
